export type GameCategory = 'word' | 'math';

export type GameId =
  | 'cryptogram'
  | 'wordCircle'
  | 'wordSearch'
  | 'wordLadder'
  | 'hashtag'
  | 'wordBloom'
  | 'codeWord'
  | 'rootwords'
  | 'wordDominoes'
  | 'emojiAnagram'
  | 'groupThink'
  | 'wordChain'
  | 'numberCrunch'
  | 'topAnswer'
  | 'cipher'
  | 'blackbox'
  | 'pivot'
  | 'longshot';

export interface GameInfo {
  id: GameId;
  title: string;
  tagline: string;
  accentVar: string;
  emoji: string;
  category: GameCategory;
  progressKey: string;
  progressLabel: (n: number) => string;
  isNew: boolean;
  isFeatured?: boolean;
  /**
   * False for games that are never published to `dailySchedule.games`, so they
   * get no tile in the daily grid — mirrors `hasDailyMode` in the iOS
   * GameRegistry. That is the Quest Gauntlet steps, and Longshot, which serves
   * its own daily board from R2 and has its own hub card.
   */
  hasDailyMode?: boolean;
}

export const GAMES: GameInfo[] = [
  {
    id: 'cryptogram',
    title: 'Cryptogram',
    tagline: 'Crack the cipher, reveal the quote',
    accentVar: 'var(--yellow)',
    emoji: '🔢',
    category: 'word',
    progressKey: 'currentCryptogramIndex',
    progressLabel: (n) => `Puzzle ${n}`,
    isNew: false,
    isFeatured: true,
  },
  {
    id: 'wordCircle',
    title: 'Word Circle',
    tagline: 'Connect letters, fill the grid',
    accentVar: 'var(--teal)',
    emoji: '🔵',
    category: 'word',
    progressKey: 'currentWordCircleLevel',
    progressLabel: (n) => `Level ${n}`,
    isNew: false,
    isFeatured: true,
  },
  {
    id: 'wordSearch',
    title: 'Word Search',
    tagline: 'Find hidden words in the grid',
    accentVar: 'var(--coral)',
    emoji: '🔍',
    category: 'word',
    progressKey: 'currentWordSearchLevel',
    progressLabel: (n) => `Level ${n}`,
    isNew: true,
  },
  {
    id: 'wordLadder',
    title: 'Word Ladder',
    tagline: 'Transform one word into another',
    accentVar: 'var(--purple)',
    emoji: '🪜',
    category: 'word',
    progressKey: 'currentWordLadderLevel',
    progressLabel: (n) => `Level ${n}`,
    isNew: true,
  },
  {
    id: 'hashtag',
    title: 'Hashtag',
    tagline: 'Swap tiles, form four words',
    accentVar: 'var(--mint)',
    emoji: '#️⃣',
    category: 'word',
    progressKey: 'currentHashtagLevel',
    progressLabel: (n) => `Level ${n}`,
    isNew: true,
  },
  {
    id: 'wordBloom',
    title: 'Word Bloom',
    tagline: 'Flower letters, bloom into words',
    accentVar: 'var(--pink)',
    emoji: '🌸',
    category: 'word',
    progressKey: 'currentWordBloomLevel',
    progressLabel: (n) => `Level ${n}`,
    isNew: true,
  },
  {
    id: 'codeWord',
    title: 'Code Word',
    tagline: 'Crack the hidden 6 or 7-letter word',
    accentVar: 'var(--yellow)',
    emoji: '🔐',
    category: 'word',
    progressKey: 'currentCodeWordLevel',
    progressLabel: (n) => `Level ${n}`,
    isNew: true,
  },
  {
    id: 'rootwords',
    title: 'Rootwords',
    tagline: 'Find words built from a shared root',
    accentVar: 'var(--teal)',
    emoji: '🌳',
    category: 'word',
    progressKey: 'currentRootwordsLevel',
    progressLabel: (n) => `Level ${n}`,
    isNew: true,
  },
  {
    id: 'wordDominoes',
    title: 'Word Dominoes',
    tagline: 'Chain words by overlapping letters',
    accentVar: 'var(--coral)',
    emoji: '🁢',
    category: 'word',
    progressKey: 'currentWordDominoesLevel',
    progressLabel: (n) => `Level ${n}`,
    isNew: true,
  },
  {
    id: 'emojiAnagram',
    title: 'Emoji Anagram',
    tagline: 'Decode the emojis, spell the word',
    accentVar: 'var(--peri)',
    emoji: '🔤',
    category: 'word',
    progressKey: 'currentEmojiAnagramLevel',
    progressLabel: (n) => `Level ${n}`,
    isNew: true,
  },
  {
    id: 'groupThink',
    title: 'Groupthink',
    tagline: 'Sort 16 words into 4 hidden groups',
    accentVar: 'var(--peri)',
    emoji: '🔲',
    category: 'word',
    progressKey: 'currentGroupThinkLevel',
    progressLabel: (n) => `Puzzle ${n}`,
    isNew: true,
  },
  {
    id: 'wordChain',
    title: 'Word Chains',
    tagline: 'Link each word to the next',
    accentVar: 'var(--teal)',
    emoji: '🔗',
    category: 'word',
    progressKey: 'currentWordChainLevel',
    progressLabel: (n) => `Level ${n}`,
    isNew: true,
  },
  {
    id: 'numberCrunch',
    title: 'Number Crunch',
    tagline: 'Combine numbers to hit the target',
    accentVar: 'var(--purple)',
    emoji: '🔢',
    category: 'math',
    progressKey: 'currentNumberCrunchLevel',
    progressLabel: (n) => `Level ${n}`,
    isNew: true,
  },
  {
    id: 'topAnswer',
    title: 'Top Answer',
    tagline: 'Guess what most people said',
    accentVar: 'var(--yellow)',
    emoji: '📊',
    category: 'word',
    progressKey: 'currentTopAnswerLevel',
    progressLabel: (n) => `Question ${n}`,
    isNew: true,
  },

  // --- Quest Gauntlet only. No hub tile: these are never published to
  //     `dailySchedule.games`, only to `dailySchedule.quest.steps`.
  {
    id: 'cipher',
    title: 'Cipher',
    tagline: 'Crack the substitution, decode the quote',
    accentVar: 'var(--pink)',
    emoji: '🔒',
    category: 'word',
    progressKey: 'currentCipherLevel',
    progressLabel: (n) => `Puzzle ${n}`,
    isNew: true,
    hasDailyMode: false,
  },
  {
    id: 'blackbox',
    title: 'Blackbox',
    tagline: 'Find the hidden rule',
    accentVar: 'var(--coral)',
    emoji: '📦',
    category: 'word',
    progressKey: 'currentBlackboxLevel',
    progressLabel: (n) => `Puzzle ${n}`,
    isNew: true,
    hasDailyMode: false,
  },
  {
    id: 'pivot',
    title: 'Pivot',
    tagline: 'Transform start word into target via swap, slide, bend',
    accentVar: 'var(--mint)',
    emoji: '🔀',
    category: 'word',
    progressKey: 'currentPivotLevel',
    progressLabel: (n) => `Puzzle ${n}`,
    isNew: true,
    hasDailyMode: false,
  },

  // --- Special event. Not in the Daily Challenge rotation: its board comes from
  //     R2 (`v1/longshot/{date}.json`), not Firestore, and the hub gives it its
  //     own card — the web counterpart of the iOS Special Events row.
  {
    id: 'longshot',
    title: 'Longshot',
    tagline: 'Five bullets, five words, one ladder',
    accentVar: 'var(--coral)',
    emoji: '🎯',
    category: 'word',
    progressKey: 'currentLongshotDay',
    progressLabel: (n) => `Day ${n}`,
    isNew: true,
    hasDailyMode: false,
  },
];

/** Games eligible to appear in the Daily Challenge — the hub tile list. */
export const DAILY_GAMES: GameInfo[] = GAMES.filter((g) => g.hasDailyMode !== false);

export const GAME_BY_ID: Record<GameId, GameInfo> = GAMES.reduce(
  (acc, g) => ({ ...acc, [g.id]: g }),
  {} as Record<GameId, GameInfo>,
);
